/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomButtonV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Button } from "@mui/material";
import React from "react";
import PropTypes from "prop-types";



const BUTTON_TYPE_STYLES = {
  submit: {
    backgroundColor: "#26890D",
    color: "white",
    "&:hover": { backgroundColor: "#20730B" },
    "&.Mui-disabled": {
      backgroundColor: "#A8D09E",
      color: "white",
      cursor: "not-allowed",
    },
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
  },

  cancel: {
    backgroundColor: "transparent",
    color: "#000000",
    border: "2px solid #000000",
    "&:hover": {
      borderColor: "#000000",
      color: "#000000",
      backgroundColor: "#EBEBEB",
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      borderColor: "#DDDDDD",
      color: "#DDDDDD",
      cursor: "not-allowed",
    },
  },

  other: {
    backgroundColor: "transparent",
    color: "#26890D",
    border: "2px solid #26890D",
    "&:hover": {
      borderColor: "#26890D",
      color: "#26890D",
      backgroundColor: "#EBEBEB",
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      borderColor: "#DDDDDD",
      color: "#DDDDDD",
      cursor: "not-allowed",
    },
  },

  error: {
    backgroundColor: "#DA291C",
    color: "white",
    "&:hover": { backgroundColor: "#DA291C" },
    "&.Mui-disabled": {
      backgroundColor: "#A8D09E",
      color: "white",
      cursor: "not-allowed",
    },
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
  },

  "blue-outline": {
    backgroundColor: "transparent",
    color: "#007CB0",
    border: "2px solid #007CB0",
    "&:hover": {
      borderColor: "#007CB0",
      color: "#007CB0",
      backgroundColor: "#EBEBEB",
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      borderColor: "#DDDDDD",
      color: "#DDDDDD",
      cursor: "not-allowed",
    },
  },
};

function CustomButtonV2({
  endIcon,
  startIcon,
  size = "small",
  variant = "contained",
  type = "submit",
  btnName,
  handleOnClick,
  onClick,
  disabled = false,
  btnStyle,
}) {
  const getButtonSize = () => {
    switch (size) {
      case "small":
        return { height: "30px" };
      case "medium":
      case "large":
        return { height: "40px" };
      default:
        return { height: "30px" };
    }
  };

  const typeStyles = BUTTON_TYPE_STYLES[type] || {};

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleOnClick || onClick}
      disabled={disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={{
        textTransform: "none",
        borderRadius: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "'Open Sans', sans-serif",
        fontSize: "14px",

        ...(btnName ? getButtonSize() : { width: "32px", height: "32px" }),

        ...typeStyles,

        "& .MuiButton-startIcon": {
          marginRight: btnName ? undefined : 0,
        },

        ...(btnStyle),
      }}
    >
      {btnName}
    </Button>
  );
}

CustomButtonV2.propTypes = {
  endIcon: PropTypes.node,
  startIcon: PropTypes.node,
  size: PropTypes.string,
  variant: PropTypes.string,
  type: PropTypes.string,
  btnName: PropTypes.node,
  handleOnClick: PropTypes.func,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  btnStyle: PropTypes.object,
};

CustomButtonV2.defaultProps = {
  endIcon: null,
  startIcon: null,
  size: "small",
  variant: "contained",
  type: "submit",
  btnName: null,
  handleOnClick: () => {},
  onClick: null,
  disabled: false,
  btnStyle: {},
};

export default CustomButtonV2;
